import interactiveSurveyReducer from "@/features/auth/interactiveSurveySlice";
import commentsReducer from "@/features/comments/commentsSlice";
import leaseCompsReducer from "@/features/leaseComps/leaseCompsSlice";
import leaseMediaReducer from "@/features/leaseMedia/leaseMediaSlice";
import mediaReducer from "@/features/media/mediaSlice";
import poiReducer from "@/features/poi/poiSlice";
import bingMapReducer from "@/features/survey/bingMapSlice";
import {
  interactiveSurveyApi,
  mediaApi,
  surveyApi,
  userApi,
} from "@/services/base";
import { commentsApi } from "@/services/commentsService";
import { interactiveSurveyAuth } from "@/services/interactiveSurveyAuth";
import { leaseCompsApi } from "@/services/leaseCompsService";
import { mapKeysApi } from "@/services/mapKeysService";
import { poiApi } from "@/services/poiService";
import { polygonApi } from "@/services/polygonService";
import type { Action, ThunkAction } from "@reduxjs/toolkit";
import { combineSlices, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

// Use reducerPath directly for each API
const rootReducer = combineSlices({
  [surveyApi.reducerPath]: surveyApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [mediaApi.reducerPath]: mediaApi.reducer,
  [interactiveSurveyApi.reducerPath]: interactiveSurveyApi.reducer,
  [interactiveSurveyAuth.reducerPath]: interactiveSurveyAuth.reducer,
  [commentsApi.reducerPath]: commentsApi.reducer,
  [leaseCompsApi.reducerPath]: leaseCompsApi.reducer,
  [mapKeysApi.reducerPath]: mapKeysApi.reducer,
  [poiApi.reducerPath]: poiApi.reducer,
  [polygonApi.reducerPath]: polygonApi.reducer,
  interactiveSurvey: interactiveSurveyReducer,
  media: mediaReducer,
  comments: commentsReducer,
  leaseComps: leaseCompsReducer,
  leaseMedia: leaseMediaReducer,
  poi: poiReducer,
  bingMap: bingMapReducer,
});

// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof rootReducer>;

// The store setup is wrapped in `makeStore` to allow reuse
// when setting up tests that need the same store config
export const makeStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: rootReducer,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({
        serializableCheck: {
          // Ignore these action types
          ignoredActions: [
            // Ignore RTK Query action types that might contain non-serializable data
            "mediaApi/executeQuery/fulfilled",
            "mediaApi/executeQuery/pending",
            "mediaApi/executeQuery/rejected",
          ],
          // Ignore these paths in the state - matches the exact error path pattern
          ignoredPaths: [
            // Match any path that contains getEdpTransformedImage with dynamic query params
            /mediaApi\.queries\.getEdpTransformedImage\(.+\)\.data/,
            // Also match the broader path
            /mediaApi\.queries\.getEdpTransformedImage/,
          ],
        },
      })
        .concat(surveyApi.middleware)
        .concat(userApi.middleware)
        .concat(mediaApi.middleware)
        .concat(interactiveSurveyApi.middleware)
        .concat(interactiveSurveyAuth.middleware)
        .concat(commentsApi.middleware)
        .concat(leaseCompsApi.middleware)
        .concat(mapKeysApi.middleware)
        .concat(poiApi.middleware)
        .concat(polygonApi.middleware);
    },
    preloadedState,
  });
  // configure listeners using the provided defaults
  // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
  setupListeners(store.dispatch);
  return store;
};

export const store = makeStore();

// Infer the type of `store`
export type AppStore = typeof store;
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
